// Global (user-scoped) settings page.
//
// Today: just the GitHub App connection. Lives at /account/settings —
// sibling to /account/usage. Adding new sections (Notifications, API
// keys, etc.) is one GlassPanel per concern.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	ArrowRight,
	CheckCircle2,
	Copy,
	ExternalLink,
	Github,
	Loader2,
	Mail,
	MessageSquare,
	Pause,
	Pencil,
	Plus,
	RotateCcw,
	Trash2,
	Users,
} from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";

import { GlassPanel, Pill } from "@/components/glass";
import { toast } from "@/components/toast";
import { authClient, useSession } from "@/lib/auth-client";

interface AppInfo {
	slug: string;
	name: string;
	html_url: string;
	install_url: string;
}

interface GithubInstallation {
	id: string;
	installationId: number;
	accountLogin: string;
	accountType: string; // "User" | "Organization"
	repositorySelection: string | null; // "all" | "selected"
	suspendedAt: number | null;
	createdAt: number;
}

interface Project {
	id: string;
	name: string;
	organizationId?: string | null;
}

interface Workspace {
	id: string;
	name: string;
	slug: string;
	role: string;
	createdAt: number;
}

interface WorkspaceMember {
	id: string;
	role: string;
	createdAt: number;
	user: {
		id: string;
		name: string;
		email: string;
		image: string | null;
	} | null;
}

async function fetchAppInfo(): Promise<AppInfo> {
	const r = await fetch("/api/github/app", { credentials: "include" });
	if (!r.ok) throw new Error(`github app info: ${r.status}`);
	return r.json();
}

async function fetchInstallations(): Promise<GithubInstallation[]> {
	const r = await fetch("/api/github/installations", {
		credentials: "include",
	});
	if (!r.ok) throw new Error(`github installations: ${r.status}`);
	const body = (await r.json()) as { installations: GithubInstallation[] };
	return body.installations;
}

async function fetchProjects(): Promise<Project[]> {
	const r = await fetch("/api/projects", { credentials: "include" });
	if (!r.ok) throw new Error(`projects: ${r.status}`);
	return ((await r.json()) as { projects: Project[] }).projects;
}

async function fetchWorkspaces(): Promise<Workspace[]> {
	const r = await fetch("/api/workspaces", { credentials: "include" });
	if (!r.ok) throw new Error(`workspaces: ${r.status}`);
	return ((await r.json()) as { workspaces: Workspace[] }).workspaces;
}

async function fetchWorkspaceMembers(
	workspaceId: string,
): Promise<WorkspaceMember[]> {
	const r = await fetch(`/api/workspaces/${workspaceId}/members`, {
		credentials: "include",
	});
	if (!r.ok) throw new Error(`workspace members: ${r.status}`);
	return ((await r.json()) as { members: WorkspaceMember[] }).members;
}

// Shared input/select style — matches the glass language (hairline border,
// surface fill, subtle focus). Used everywhere a form field appears so inputs
// read as one system instead of bespoke per section.
const FIELD =
	"h-9 px-3 rounded-[10px] bg-[var(--color-surface)] border border-[var(--color-border)] text-[13px] text-[var(--color-text)] placeholder:text-[var(--color-text-dim)] outline-none focus:border-[var(--color-border-hi)] transition-colors";

const SETTINGS_TABS = [
	{ id: "general", label: "General" },
	{ id: "integrations", label: "Integrations" },
	{ id: "advanced", label: "Advanced" },
] as const;
type SettingsTab = (typeof SETTINGS_TABS)[number]["id"];

export default function AccountSettings() {
	// OAuth round-trips land back here with ?slack=ok / ?gmail=ok etc. — open
	// the Integrations tab so the user sees the result.
	const cameFromConnect =
		typeof window !== "undefined" &&
		/[?&](slack|discord|gmail|github)=/.test(window.location.search);
	const [tab, setTab] = useState<SettingsTab>(
		cameFromConnect ? "integrations" : "general",
	);

	return (
		<div className="h-full overflow-y-auto pt-1 pb-8">
			<div className="max-w-[800px] mx-auto">
				<h1 className="text-[18px] font-medium tracking-tight mt-2 mb-4">
					Account settings
				</h1>

				{/* Glass segmented tab control */}
				<div className="inline-flex items-center gap-1 p-1 mb-5 rounded-[var(--radius-pill)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-glass-inner)]">
					{SETTINGS_TABS.map((t) => (
						<button
							key={t.id}
							type="button"
							onClick={() => setTab(t.id)}
							className={`px-3.5 py-1.5 rounded-[var(--radius-pill)] text-[13px] transition-colors ${
								tab === t.id
									? "bg-[var(--color-surface-hi)] text-[var(--color-text)] border border-[var(--color-border-hi)]"
									: "border border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
							}`}
						>
							{t.label}
						</button>
					))}
				</div>

				<div className="space-y-4">
					{tab === "general" && <WorkspaceSection />}
					{tab === "integrations" && (
						<>
							<GithubSection />
							<GmailSection />
							<FirefliesSection />
							<IntegrationsSection />
						</>
					)}
					{tab === "advanced" && <DebugSection />}
				</div>
			</div>
		</div>
	);
}

function WorkspaceSection() {
	const qc = useQueryClient();
	const session = useSession();
	const workspacesQ = useQuery({
		queryKey: ["workspaces"],
		queryFn: fetchWorkspaces,
	});
	const activeWorkspaceId = (
		session.data?.session as { activeOrganizationId?: string | null } | null
	)?.activeOrganizationId;
	const workspaces = workspacesQ.data ?? [];
	const activeWorkspace =
		workspaces.find((workspace) => workspace.id === activeWorkspaceId) ??
		workspaces[0] ??
		null;
	const membersQ = useQuery({
		queryKey: ["workspace-members", activeWorkspace?.id],
		queryFn: () => fetchWorkspaceMembers(activeWorkspace!.id),
		enabled: !!activeWorkspace?.id,
	});
	const [newName, setNewName] = useState("");
	const [creating, setCreating] = useState(false);
	const [editingId, setEditingId] = useState<string | null>(null);
	const [renameValue, setRenameValue] = useState("");

	const switchWorkspace = async (id: string) => {
		await authClient.organization.setActive({ organizationId: id });
		await qc.invalidateQueries({ queryKey: ["session"] });
		await qc.invalidateQueries({ queryKey: ["organizations"] });
		await qc.invalidateQueries({ queryKey: ["projects"] });
		await authClient.getSession().catch(() => {});
	};

	const createWorkspace = useMutation({
		mutationFn: async () => {
			const r = await fetch("/api/workspaces", {
				method: "POST",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ name: newName.trim() }),
			});
			if (!r.ok) throw new Error(`workspace create: ${r.status}`);
		},
		onSuccess: async () => {
			setNewName("");
			setCreating(false);
			await qc.invalidateQueries({ queryKey: ["workspaces"] });
			await qc.invalidateQueries({ queryKey: ["organizations"] });
			await qc.invalidateQueries({ queryKey: ["projects"] });
			await qc.invalidateQueries({ queryKey: ["session"] });
			await authClient.getSession().catch(() => {});
			toast.success("Workspace created");
		},
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't create workspace" }),
	});

	const renameWorkspace = useMutation({
		mutationFn: async (id: string) => {
			const r = await fetch(`/api/workspaces/${id}`, {
				method: "PATCH",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ name: renameValue.trim() }),
			});
			if (!r.ok) throw new Error(`workspace rename: ${r.status}`);
		},
		onSuccess: async () => {
			setEditingId(null);
			await qc.invalidateQueries({ queryKey: ["workspaces"] });
			await qc.invalidateQueries({ queryKey: ["organizations"] });
			toast.success("Workspace renamed");
		},
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't rename workspace" }),
	});

	return (
		<GlassPanel className="p-5 space-y-5">
			<div className="flex items-start gap-3">
				<div className="w-8 h-8 rounded-[8px] bg-white/[0.04] border border-[var(--color-border)] flex items-center justify-center shrink-0">
					<Users className="w-4 h-4 text-[var(--color-text)]" />
				</div>
				<div>
					<div className="text-[14px] font-medium">Workspaces</div>
					<div className="text-[12px] text-[var(--color-text-muted)] mt-0.5">
						New projects are created in the active workspace.
					</div>
				</div>
			</div>

			{/* Workspace list — one row each; the active row renames inline. */}
			<div className="space-y-1.5">
				{workspaces.map((workspace) => {
					const active = workspace.id === activeWorkspace?.id;
					const editing = editingId === workspace.id;
					return (
						<div
							key={workspace.id}
							className={`flex items-center gap-3 px-3 py-2.5 rounded-[10px] border transition-colors ${
								active
									? "border-[var(--color-border-hi)] bg-[var(--color-surface-hi)]"
									: "border-[var(--color-border)]"
							}`}
						>
							{editing ? (
								<>
									<input
										ref={(el) => el?.focus()}
										value={renameValue}
										onChange={(e) => setRenameValue(e.target.value)}
										onKeyDown={(e) => {
											if (e.key === "Enter")
												renameWorkspace.mutate(workspace.id);
											if (e.key === "Escape") setEditingId(null);
										}}
										className={`flex-1 ${FIELD}`}
									/>
									<Pill
										onClick={() => renameWorkspace.mutate(workspace.id)}
										disabled={
											!renameValue.trim() ||
											renameValue.trim() === workspace.name ||
											renameWorkspace.isPending
										}
									>
										{renameWorkspace.isPending ? (
											<Loader2 className="w-3.5 h-3.5 animate-spin" />
										) : (
											"Save"
										)}
									</Pill>
									<button
										type="button"
										onClick={() => setEditingId(null)}
										className="text-[12px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] px-1"
									>
										Cancel
									</button>
								</>
							) : (
								<>
									<div className="min-w-0 flex-1">
										<div className="flex items-center gap-2">
											<span className="text-[13px] truncate">
												{workspace.name}
											</span>
											{active && (
												<span className="text-[9px] uppercase tracking-wider text-[var(--color-text-dim)] border border-[var(--color-border)] rounded-full px-1.5 py-0.5 shrink-0">
													Active
												</span>
											)}
										</div>
										<div className="text-[11px] text-[var(--color-text-dim)] mt-0.5">
											{workspace.role}
										</div>
									</div>
									{active ? (
										<button
											type="button"
											title="Rename"
											onClick={() => {
												setRenameValue(workspace.name);
												setEditingId(workspace.id);
											}}
											className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] shrink-0 p-1"
										>
											<Pencil className="w-3.5 h-3.5" />
										</button>
									) : (
										<Pill onClick={() => switchWorkspace(workspace.id)}>
											Switch
										</Pill>
									)}
								</>
							)}
						</div>
					);
				})}

				{/* Create — a quiet affordance that reveals an inline field. */}
				{creating ? (
					<div className="flex items-center gap-2 pt-0.5">
						<input
							ref={(el) => el?.focus()}
							value={newName}
							onChange={(e) => setNewName(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter" && newName.trim())
									createWorkspace.mutate();
								if (e.key === "Escape") {
									setCreating(false);
									setNewName("");
								}
							}}
							placeholder="Workspace name"
							className={`flex-1 ${FIELD}`}
						/>
						<Pill
							onClick={() => createWorkspace.mutate()}
							disabled={!newName.trim() || createWorkspace.isPending}
						>
							{createWorkspace.isPending ? (
								<Loader2 className="w-3.5 h-3.5 animate-spin" />
							) : (
								"Create"
							)}
						</Pill>
						<button
							type="button"
							onClick={() => {
								setCreating(false);
								setNewName("");
							}}
							className="text-[12px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] px-1"
						>
							Cancel
						</button>
					</div>
				) : (
					<button
						type="button"
						onClick={() => setCreating(true)}
						className="flex items-center gap-1.5 px-3 py-2 text-[12px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
					>
						<Plus className="w-3.5 h-3.5" />
						New workspace
					</button>
				)}
			</div>

			{/* Members of the active workspace. */}
			{activeWorkspace && (
				<div className="space-y-3 pt-4 border-t border-[var(--color-border)]">
					<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
						Members
					</div>
					<div className="space-y-2.5">
						{(membersQ.data ?? []).map((member) => (
							<div
								key={member.id}
								className="flex items-center justify-between gap-3"
							>
								<div className="flex items-center gap-2.5 min-w-0">
									<div className="w-7 h-7 rounded-full bg-[var(--color-surface-solid)] border border-[var(--color-border)] flex items-center justify-center text-[11px] text-[var(--color-text-muted)] shrink-0">
										{(member.user?.name ?? "?").charAt(0).toUpperCase()}
									</div>
									<div className="min-w-0">
										<div className="text-[13px] truncate">
											{member.user?.name ?? "Unknown user"}
										</div>
										<div className="text-[11px] text-[var(--color-text-dim)] truncate">
											{member.user?.email ?? "—"}
										</div>
									</div>
								</div>
								<div className="text-[10px] text-[var(--color-text-dim)] uppercase tracking-wider shrink-0">
									{member.role}
								</div>
							</div>
						))}
						{membersQ.isLoading && (
							<div className="text-[12px] text-[var(--color-text-muted)]">
								Loading members…
							</div>
						)}
					</div>
				</div>
			)}
		</GlassPanel>
	);
}

function GithubSection() {
	const qc = useQueryClient();
	const appQ = useQuery({
		queryKey: ["github-app"],
		queryFn: fetchAppInfo,
		staleTime: 5 * 60 * 1000, // app info doesn't change
	});
	const installsQ = useQuery({
		queryKey: ["github-installations"],
		queryFn: fetchInstallations,
		// Refetch on focus — when the user returns from the GitHub install
		// page, our webhook has just landed and the new install should appear.
		refetchOnWindowFocus: true,
	});

	const installs = installsQ.data ?? [];
	const hasInstalls = installs.length > 0;

	function openInstall() {
		if (!appQ.data) return;
		window.open(appQ.data.install_url, "_blank", "noopener,noreferrer");
		// Re-poll a few times in the background to catch the webhook landing.
		const start = Date.now();
		const interval = setInterval(() => {
			qc.invalidateQueries({ queryKey: ["github-installations"] });
			if (Date.now() - start > 60_000) clearInterval(interval);
		}, 3_000);
	}

	return (
		<GlassPanel className="p-5 space-y-4">
			<div className="flex items-start justify-between gap-4">
				<div className="flex items-start gap-3">
					<div className="w-8 h-8 rounded-[8px] bg-white/[0.04] border border-[var(--color-border)] flex items-center justify-center shrink-0">
						<Github className="w-4 h-4 text-[var(--color-text)]" />
					</div>
					<div>
						<div className="text-[14px] font-medium">GitHub</div>
						<div className="text-[12px] text-[var(--color-text-muted)] mt-0.5">
							Connect your GitHub account so projects can link to repositories.
						</div>
					</div>
				</div>
				<Pill onClick={openInstall} disabled={!appQ.data}>
					<ExternalLink className="w-3.5 h-3.5" />
					{hasInstalls ? "Install on another account" : "Install GitHub App"}
				</Pill>
			</div>

			{installsQ.isLoading ? (
				<div className="flex items-center gap-2 py-3 text-[13px] text-[var(--color-text-muted)]">
					<Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
				</div>
			) : installsQ.error ? (
				<div className="text-[13px] text-[var(--color-danger)] py-4 text-center border border-dashed border-[rgba(239,68,68,0.35)] rounded-[8px]">
					Could not load installations. {(installsQ.error as Error).message}
				</div>
			) : hasInstalls ? (
				<div className="space-y-2 pt-1">
					{installs.map((inst) => (
						<InstallationRow key={inst.id} install={inst} />
					))}
				</div>
			) : (
				<div className="text-[13px] text-[var(--color-text-muted)] py-4 text-center border border-dashed border-[var(--color-border)] rounded-[8px]">
					No GitHub accounts connected yet. Install the app on a user or
					organization to get started.
				</div>
			)}
		</GlassPanel>
	);
}

function InstallationRow({ install }: { install: GithubInstallation }) {
	const suspended = install.suspendedAt != null;
	const repoMode =
		install.repositorySelection === "all" ? "all repos" : "selected repos";

	return (
		<div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-[8px] border border-[var(--color-border)] bg-white/[0.02]">
			<div className="flex items-center gap-3 min-w-0">
				<div className="w-6 h-6 rounded-full bg-[var(--color-surface-solid)] border border-[var(--color-border)] flex items-center justify-center shrink-0">
					{install.accountType === "Organization" ? (
						<Github className="w-3 h-3" />
					) : (
						<span className="text-[10px] mono">
							{install.accountLogin[0]?.toUpperCase()}
						</span>
					)}
				</div>
				<div className="min-w-0">
					<div className="text-[13px] truncate">{install.accountLogin}</div>
					<div className="text-[11px] text-[var(--color-text-dim)] mt-0.5">
						{install.accountType.toLowerCase()} · {repoMode}
					</div>
				</div>
			</div>
			<div className="flex items-center gap-1.5 text-[11px]">
				{suspended ? (
					<>
						<Pause className="w-3 h-3 text-[var(--color-text-muted)]" />
						<span className="text-[var(--color-text-muted)]">suspended</span>
					</>
				) : (
					<>
						<CheckCircle2 className="w-3 h-3 text-emerald-400/80" />
						<span className="text-[var(--color-text-muted)]">active</span>
					</>
				)}
			</div>
		</div>
	);
}

interface GmailAccount {
	id: string;
	email: string;
	createdAt: number;
	updatedAt: number;
	revokedAt: number | null;
}

async function fetchGmailAccounts(): Promise<GmailAccount[]> {
	const r = await fetch("/api/gmail/accounts", { credentials: "include" });
	if (!r.ok) throw new Error(`gmail accounts: ${r.status}`);
	const body = (await r.json()) as { accounts: GmailAccount[] };
	return body.accounts;
}

function GmailSection() {
	const qc = useQueryClient();
	const accountsQ = useQuery({
		queryKey: ["gmail-accounts"],
		queryFn: fetchGmailAccounts,
		refetchOnWindowFocus: true,
	});

	const accounts = accountsQ.data ?? [];
	const hasAccount = accounts.some((a) => !a.revokedAt);

	const connect = useMutation({
		mutationFn: async () => {
			const r = await fetch("/api/gmail/connect", { credentials: "include" });
			if (!r.ok) throw new Error(`gmail connect: ${r.status}`);
			const body = (await r.json()) as { url?: string };
			if (!body.url) throw new Error("no oauth url returned");
			window.open(body.url, "_blank", "noopener,noreferrer");
			// Re-poll a few times so the new account row shows up
			// after the redirect-back.
			const start = Date.now();
			const interval = setInterval(() => {
				qc.invalidateQueries({ queryKey: ["gmail-accounts"] });
				if (Date.now() - start > 60_000) clearInterval(interval);
			}, 3_000);
		},
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't open Gmail consent screen" }),
	});

	const disconnect = useMutation({
		mutationFn: async (id: string) => {
			const r = await fetch(`/api/gmail/accounts/${id}`, {
				method: "DELETE",
				credentials: "include",
			});
			if (!r.ok) throw new Error(`gmail disconnect: ${r.status}`);
		},
		onSuccess: () => {
			toast.success("Gmail disconnected");
			qc.invalidateQueries({ queryKey: ["gmail-accounts"] });
		},
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't disconnect Gmail" }),
	});

	const resync = useMutation({
		mutationFn: async (id: string) => {
			const r = await fetch(`/api/gmail/accounts/${id}/baseline-sync`, {
				method: "POST",
				credentials: "include",
			});
			if (!r.ok) throw new Error(`gmail resync: ${r.status}`);
		},
		onSuccess: () => toast.success("Baseline sync started"),
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't start resync" }),
	});

	return (
		<GlassPanel className="p-5 space-y-4">
			<div className="flex items-start justify-between gap-4">
				<div className="flex items-start gap-3">
					<div className="w-8 h-8 rounded-[8px] bg-white/[0.04] border border-[var(--color-border)] flex items-center justify-center shrink-0">
						<Mail className="w-4 h-4 text-[var(--color-text)]" />
					</div>
					<div>
						<div className="text-[14px] font-medium">Gmail</div>
						<div className="text-[12px] text-[var(--color-text-muted)] mt-0.5">
							Read-only access to your inbox. Ingests message metadata (headers
							+ snippet) — not bodies.
						</div>
					</div>
				</div>
				<Pill onClick={() => connect.mutate()} disabled={connect.isPending}>
					{connect.isPending ? (
						<Loader2 className="w-3.5 h-3.5 animate-spin" />
					) : (
						<ExternalLink className="w-3.5 h-3.5" />
					)}
					{hasAccount ? "Reconnect" : "Connect Gmail"}
				</Pill>
			</div>

			{accountsQ.isLoading ? (
				<div className="flex items-center gap-2 py-3 text-[13px] text-[var(--color-text-muted)]">
					<Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
				</div>
			) : accountsQ.error ? (
				<div className="text-[13px] text-[var(--color-danger)] py-4 text-center border border-dashed border-[rgba(239,68,68,0.35)] rounded-[8px]">
					Could not load Gmail accounts. {(accountsQ.error as Error).message}
				</div>
			) : accounts.length > 0 ? (
				<div className="space-y-2 pt-1">
					{accounts.map((acct) => {
						const revoked = acct.revokedAt != null;
						return (
							<div
								key={acct.id}
								className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-[8px] border border-[var(--color-border)] bg-white/[0.02]"
							>
								<div className="flex items-center gap-3 min-w-0">
									<div className="w-6 h-6 rounded-full bg-[var(--color-surface-solid)] border border-[var(--color-border)] flex items-center justify-center shrink-0">
										<Mail className="w-3 h-3" />
									</div>
									<div className="min-w-0">
										<div className="text-[13px] truncate">{acct.email}</div>
										<div className="text-[11px] text-[var(--color-text-dim)] mt-0.5 flex items-center gap-1.5">
											{revoked ? (
												<>
													<Pause className="w-3 h-3" />
													<span>token revoked</span>
												</>
											) : (
												<>
													<CheckCircle2 className="w-3 h-3 text-emerald-400/80" />
													<span>active</span>
												</>
											)}
										</div>
									</div>
								</div>
								<div className="flex items-center gap-1.5">
									<button
										type="button"
										onClick={() => resync.mutate(acct.id)}
										disabled={resync.isPending || revoked}
										className="text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40 inline-flex items-center gap-1"
									>
										<RotateCcw className="w-3 h-3" />
										Resync
									</button>
									<button
										type="button"
										onClick={() => disconnect.mutate(acct.id)}
										disabled={disconnect.isPending}
										className="text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-danger)] disabled:opacity-40 inline-flex items-center gap-1"
									>
										<Trash2 className="w-3 h-3" />
										Disconnect
									</button>
								</div>
							</div>
						);
					})}
				</div>
			) : (
				<div className="text-[13px] text-[var(--color-text-muted)] py-4 text-center border border-dashed border-[var(--color-border)] rounded-[8px]">
					No Gmail account connected yet.
				</div>
			)}
		</GlassPanel>
	);
}

// Official Fireflies.ai brand mark (24x24, gradient). Inlined because
// simple-icons doesn't carry it. Keep the gradient ids namespaced so two
// instances on the page don't collide.
function FirefliesIcon({ className = "w-4 h-4" }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			className={className}
			aria-hidden="true"
		>
			<title>Fireflies</title>
			<path d="M8.40366 1.5L1.5 1.5L1.5 8.35301H8.40366V1.5Z" fill="url(#ff_p0)" />
			<path
				d="M16.5814 1.5L9.67773 1.5V8.35301H22.4997V7.37375C22.4996 5.81588 21.876 4.32189 20.7663 3.22037C19.6565 2.11884 18.1514 1.5 16.582 1.5H16.5814Z"
				fill="url(#ff_p1)"
			/>
			<path
				d="M1.5 9.77344L1.5 16.6264C1.50016 18.1843 2.12371 19.6783 3.23348 20.7799C4.34326 21.8814 5.84838 22.5002 7.41776 22.5002H8.40366V9.77344H1.5Z"
				fill="url(#ff_p2)"
			/>
			<path
				d="M16.5814 9.77344H9.67773V16.6265H16.5814V9.77344Z"
				fill="url(#ff_p6)"
			/>
			<defs>
				<linearGradient
					id="ff_p0"
					x1="18.3863"
					y1="18.8381"
					x2="-10.6333"
					y2="-11.652"
					gradientUnits="userSpaceOnUse"
				>
					<stop stopColor="#E82A73" />
					<stop offset="0.3" stopColor="#C5388F" />
					<stop offset="0.818" stopColor="#6262DE" />
					<stop offset="0.994" stopColor="#3B73FF" />
				</linearGradient>
				<linearGradient
					id="ff_p1"
					x1="23.3075"
					y1="14.1575"
					x2="13.4745"
					y2="-21.4763"
					gradientUnits="userSpaceOnUse"
				>
					<stop stopColor="#E82A73" />
					<stop offset="0.3" stopColor="#C5388F" />
					<stop offset="0.818" stopColor="#6262DE" />
					<stop offset="0.994" stopColor="#3B73FF" />
				</linearGradient>
				<linearGradient
					id="ff_p2"
					x1="13.6297"
					y1="23.3656"
					x2="-21.571"
					y2="12.6417"
					gradientUnits="userSpaceOnUse"
				>
					<stop stopColor="#E82A73" />
					<stop offset="0.3" stopColor="#C5388F" />
					<stop offset="0.818" stopColor="#6262DE" />
					<stop offset="0.994" stopColor="#3B73FF" />
				</linearGradient>
				<linearGradient
					id="ff_p6"
					x1="18.4868"
					y1="18.744"
					x2="-10.5328"
					y2="-11.7461"
					gradientUnits="userSpaceOnUse"
				>
					<stop stopColor="#FF3C82" />
					<stop offset="0.492" stopColor="#B251B2" />
					<stop offset="0.994" stopColor="#3B73FF" />
				</linearGradient>
			</defs>
		</svg>
	);
}

interface FirefliesAccount {
	id: string;
	label: string | null;
	projectId: string;
	webhookUrl: string;
	hasSigningSecret: boolean;
	lastEventAt: number | null;
	revokedAt: number | null;
	createdAt: number | null;
}

async function fetchFirefliesAccounts(): Promise<FirefliesAccount[]> {
	const r = await fetch("/api/integrations/fireflies/accounts", {
		credentials: "include",
	});
	if (!r.ok) throw new Error(`fireflies accounts: ${r.status}`);
	return ((await r.json()) as { accounts: FirefliesAccount[] }).accounts;
}

function CopyButton({ value, label }: { value: string; label: string }) {
	const [copied, setCopied] = useState(false);
	return (
		<button
			type="button"
			onClick={() => {
				navigator.clipboard.writeText(value).then(() => {
					setCopied(true);
					setTimeout(() => setCopied(false), 1500);
				});
			}}
			className="text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] inline-flex items-center gap-1"
		>
			{copied ? (
				<CheckCircle2 className="w-3 h-3 text-emerald-400/80" />
			) : (
				<Copy className="w-3 h-3" />
			)}
			{copied ? "Copied" : label}
		</button>
	);
}

function FirefliesSection() {
	const qc = useQueryClient();
	const accountsQ = useQuery({
		queryKey: ["fireflies-accounts"],
		queryFn: fetchFirefliesAccounts,
		refetchOnWindowFocus: true,
	});
	const projectsQ = useQuery({
		queryKey: ["projects"],
		queryFn: fetchProjects,
	});

	const accounts = accountsQ.data ?? [];
	const account = accounts.find((a) => !a.revokedAt) ?? accounts[0] ?? null;
	const projects = projectsQ.data ?? [];

	const [showForm, setShowForm] = useState(false);
	const [apiKey, setApiKey] = useState("");
	const [signingSecret, setSigningSecret] = useState("");
	const [projectId, setProjectId] = useState("");

	const connect = useMutation({
		mutationFn: async () => {
			const pid = projectId || projects[0]?.id;
			if (!pid) throw new Error("Create a project first");
			const r = await fetch("/api/integrations/fireflies/connect", {
				method: "POST",
				credentials: "include",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({
					apiKey: apiKey.trim(),
					signingSecret: signingSecret.trim() || null,
					projectId: pid,
				}),
			});
			if (!r.ok) {
				const body = (await r.json().catch(() => ({}))) as { error?: string };
				const map: Record<string, string> = {
					invalid_api_key: "That API key was rejected by Fireflies.",
					project_not_found: "Pick a project you own.",
					fireflies_unreachable: "Couldn't reach Fireflies. Try again.",
				};
				throw new Error(map[body.error ?? ""] ?? `connect failed: ${r.status}`);
			}
		},
		onSuccess: () => {
			toast.success("Fireflies connected");
			setApiKey("");
			setSigningSecret("");
			setShowForm(false);
			qc.invalidateQueries({ queryKey: ["fireflies-accounts"] });
		},
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't connect Fireflies" }),
	});

	const disconnect = useMutation({
		mutationFn: async (id: string) => {
			const r = await fetch(`/api/integrations/fireflies/accounts/${id}`, {
				method: "DELETE",
				credentials: "include",
			});
			if (!r.ok) throw new Error(`fireflies disconnect: ${r.status}`);
		},
		onSuccess: () => {
			toast.success("Fireflies disconnected");
			qc.invalidateQueries({ queryKey: ["fireflies-accounts"] });
		},
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't disconnect Fireflies" }),
	});

	const projectName = (id: string) =>
		projects.find((p) => p.id === id)?.name ?? id;

	return (
		<GlassPanel className="p-5 space-y-4">
			<div className="flex items-start justify-between gap-4">
				<div className="flex items-start gap-3">
					<div className="w-8 h-8 rounded-[8px] bg-white/[0.04] border border-[var(--color-border)] flex items-center justify-center shrink-0">
						<FirefliesIcon className="w-4 h-4" />
					</div>
					<div>
						<div className="text-[14px] font-medium">Fireflies</div>
						<div className="text-[12px] text-[var(--color-text-muted)] mt-0.5">
							Ingest meeting transcripts. A webhook tells us when a meeting is
							ready; we pull the transcript, attendees, and summary into a
							project's graph.
						</div>
					</div>
				</div>
				{!account && (
					<Pill
						onClick={() => setShowForm((v) => !v)}
						disabled={connect.isPending}
					>
						<ExternalLink className="w-3.5 h-3.5" />
						Connect
					</Pill>
				)}
			</div>

			{accountsQ.isLoading ? (
				<div className="flex items-center gap-2 py-3 text-[13px] text-[var(--color-text-muted)]">
					<Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
				</div>
			) : account ? (
				<div className="space-y-3 pt-1">
					<div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-[8px] border border-[var(--color-border)] bg-white/[0.02]">
						<div className="flex items-center gap-3 min-w-0">
							<div className="w-6 h-6 rounded-full bg-[var(--color-surface-solid)] border border-[var(--color-border)] flex items-center justify-center shrink-0">
								<FirefliesIcon className="w-3.5 h-3.5" />
							</div>
							<div className="min-w-0">
								<div className="text-[13px] truncate">
									{account.label ?? "Fireflies"}
								</div>
								<div className="text-[11px] text-[var(--color-text-dim)] mt-0.5 flex items-center gap-1.5">
									{account.revokedAt ? (
										<>
											<Pause className="w-3 h-3" />
											<span>API key revoked — reconnect</span>
										</>
									) : (
										<>
											<CheckCircle2 className="w-3 h-3 text-emerald-400/80" />
											<span>
												→ {projectName(account.projectId)}
												{account.lastEventAt
													? ` · last event ${new Date(account.lastEventAt).toLocaleDateString()}`
													: " · no events yet"}
											</span>
										</>
									)}
								</div>
							</div>
						</div>
						<div className="flex items-center gap-2.5 shrink-0">
							<button
								type="button"
								onClick={() => {
									setProjectId(account.projectId);
									setShowForm(true);
								}}
								className="text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] inline-flex items-center gap-1"
							>
								<RotateCcw className="w-3 h-3" />
								Reconnect
							</button>
							<button
								type="button"
								onClick={() => disconnect.mutate(account.id)}
								disabled={disconnect.isPending}
								className="text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-danger)] disabled:opacity-40 inline-flex items-center gap-1"
							>
								<Trash2 className="w-3 h-3" />
								Disconnect
							</button>
						</div>
					</div>

					<div className="px-3 py-2.5 rounded-[8px] border border-[var(--color-border)] bg-white/[0.02] space-y-1.5">
						<div className="flex items-center justify-between gap-3">
							<div className="text-[11px] text-[var(--color-text-muted)]">
								Webhook URL — paste into Fireflies → Settings → Developer
								Settings → Webhooks
							</div>
							<CopyButton value={account.webhookUrl} label="Copy" />
						</div>
						<code className="block text-[11px] text-[var(--color-text)] break-all font-mono">
							{account.webhookUrl}
						</code>
						{!account.hasSigningSecret && (
							<div className="text-[11px] text-[var(--color-text-dim)]">
								No signing secret set — reconnect with one to verify webhook
								authenticity.
							</div>
						)}
					</div>
				</div>
			) : (
				!showForm && (
					<div className="text-[13px] text-[var(--color-text-muted)] py-4 text-center border border-dashed border-[var(--color-border)] rounded-[8px]">
						No Fireflies account connected yet.
					</div>
				)
			)}

			{showForm && (
				<div className="space-y-2.5 pt-1">
					<div className="flex flex-col gap-1">
						<span className="text-[11px] text-[var(--color-text-muted)]">
							Project
						</span>
						<select
							className={FIELD}
							value={projectId || account?.projectId || projects[0]?.id || ""}
							onChange={(e) => setProjectId(e.target.value)}
						>
							{projects.map((p) => (
								<option key={p.id} value={p.id}>
									{p.name}
								</option>
							))}
						</select>
					</div>
					<div className="flex flex-col gap-1">
						<span className="text-[11px] text-[var(--color-text-muted)]">
							Fireflies API key
							<span className="text-[var(--color-text-dim)]">
								{" "}
								— fireflies.ai → Settings → Developer Settings
							</span>
						</span>
						<input
							className={FIELD}
							type="password"
							placeholder="fireflies api key"
							value={apiKey}
							onChange={(e) => setApiKey(e.target.value)}
						/>
					</div>
					<div className="flex flex-col gap-1">
						<span className="text-[11px] text-[var(--color-text-muted)]">
							Signing secret <span className="text-[var(--color-text-dim)]">(optional, recommended)</span>
						</span>
						<input
							className={FIELD}
							type="password"
							placeholder="the secret you set on the Fireflies webhook"
							value={signingSecret}
							onChange={(e) => setSigningSecret(e.target.value)}
						/>
					</div>
					<div className="flex items-center gap-2 pt-1">
						<Pill
							onClick={() => connect.mutate()}
							disabled={connect.isPending || apiKey.trim().length < 8}
						>
							{connect.isPending ? (
								<Loader2 className="w-3.5 h-3.5 animate-spin" />
							) : (
								<CheckCircle2 className="w-3.5 h-3.5" />
							)}
							{account ? "Reconnect" : "Connect Fireflies"}
						</Pill>
						<button
							type="button"
							onClick={() => setShowForm(false)}
							className="text-[12px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
						>
							Cancel
						</button>
					</div>
				</div>
			)}
		</GlassPanel>
	);
}

// Spec 48 — chat integrations have their own management page now; the settings
// Integrations tab just points there.
function IntegrationsSection() {
	return (
		<GlassPanel className="p-5">
			<div className="flex items-center justify-between gap-4">
				<div className="flex items-start gap-3">
					<div className="w-8 h-8 rounded-[8px] bg-white/[0.04] border border-[var(--color-border)] flex items-center justify-center shrink-0">
						<MessageSquare className="w-4 h-4 text-[var(--color-text)]" />
					</div>
					<div>
						<div className="text-[14px] font-medium">Chat integrations</div>
						<div className="text-[12px] text-[var(--color-text-muted)] mt-0.5">
							Connect Slack or Discord, scope who can use it, and view activity.
						</div>
					</div>
				</div>
				<Link to="/account/integrations" className="pill shrink-0">
					Manage
					<ArrowRight className="w-3.5 h-3.5" />
				</Link>
			</div>
		</GlassPanel>
	);
}

// Temporary surface for dogfood-phase testing. Remove (or move
// behind an admin-only gate) once onboarding is stable.
function DebugSection() {
	const navigate = useNavigate();
	const reset = useMutation({
		mutationFn: async () => {
			const r = await fetch("/api/onboarding/reset", {
				method: "POST",
				credentials: "include",
			});
			if (!r.ok) throw new Error(`reset: ${r.status}`);
		},
		onSuccess: () => {
			toast.success("Onboarding reset");
			navigate("/welcome");
		},
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't reset onboarding" }),
	});

	return (
		<GlassPanel className="p-5 space-y-3">
			<div>
				<div className="flex items-center gap-2">
					<div className="text-[13px] font-medium">Debug</div>
					<span className="text-[9px] uppercase tracking-wider text-[var(--color-text-dim)] border border-[var(--color-border)] px-1.5 py-0.5 rounded">
						temporary
					</span>
				</div>
				<div className="text-[11px] text-[var(--color-text-muted)] mt-0.5">
					Dogfood-phase shortcuts. These will be removed.
				</div>
			</div>

			<div className="flex items-center justify-between gap-3 pt-1 border-t border-[var(--color-border)]">
				<div>
					<div className="text-[12px]">Reset onboarding</div>
					<div className="text-[10px] text-[var(--color-text-dim)] mt-0.5">
						Wipes your onboarding state and sends you back to /welcome.
					</div>
				</div>
				<Pill onClick={() => reset.mutate()} disabled={reset.isPending}>
					{reset.isPending ? (
						<Loader2 className="w-3.5 h-3.5 animate-spin" />
					) : (
						<RotateCcw className="w-3.5 h-3.5" />
					)}
					Reset
				</Pill>
			</div>
		</GlassPanel>
	);
}
